import React, { useEffect, useState } from 'react';
import IApplicationReview from 'interfaces/IApplicationReview';
import IApplicationReviewForm, { IPartData } from 'interfaces/IApplicationReviewForm';
import SingleInput from '../Steps/SingleInput';
import AccordionInput from '../Steps/AccordionInput';
import PartD from '../Steps/PartD';
import Confirmation from '../Steps/Confirmation';
import ISimpleNameValuePair from 'interfaces/ISimpleNameValuePair';
import {
    IAccreditationApplicationRequirements,
    IEvaluationRequirementsGroup,
    IReaccreditationApplicationRequirements,
} from 'interfaces/IApplicationRequirements';
import { ReviewFormat, ReviewStatus } from 'graphql/__generated__/graphql';
import { IStepConfig } from '../../../Form/FormStepper/FormStepper';
import IRankingsPair from '../../../../interfaces/IRankingsPair';
import { useNavigate } from 'react-router-dom';
import useAlgorithmCalculator from './useAlgorithmCalculator';

const emptyRequirements = { config: { help: {} }, evaluation: [], experience: [], documents: [] };

function reviewToForm(applicationReview: IApplicationReview): IApplicationReviewForm {
    const { applicationReviewId, jsonData } = applicationReview;
    const appData = JSON.parse(jsonData || '{}');

    return {
        applicationReviewId: applicationReviewId,
        ...appData,
    };
}

interface OperationResult {
    success: boolean;
    message: string | string[];
}

const checkLocalStorage = (identifier?: string) => {
    if (!identifier) return;
    const backup = localStorage.getItem(identifier);

    if (!backup) return null;
    try {
        return JSON.parse(backup);
    } catch (e) {
        // return null;
    }
};

export const useSupervisorReviewFormComponent = (
    applicationReviewMaybe: IApplicationReview,
    loading: boolean,
    readOnly = false,
    update: (review: IApplicationReview) => Promise<OperationResult>
) => {
    const navigate = useNavigate();
    const { calculateScore } = useAlgorithmCalculator();

    const applicationReview =
        checkLocalStorage(applicationReviewMaybe?.applicationReviewId) || applicationReviewMaybe;

    // Application form data
    const [formData, setFormData] = useState<IApplicationReviewForm>(
        reviewToForm(applicationReview)
    );
    const [requirements, setRequirements] = useState<
        IAccreditationApplicationRequirements | IReaccreditationApplicationRequirements
    >(emptyRequirements);
    const [stepsConfig, setStepsConfig] = useState<IStepConfig[]>([]);
    const [validated, setValidated] = useState<boolean>(false);

    // Form visibility
    const [currentStep, setCurrentStep] = useState(1);

    useEffect(() => {
        if (
            applicationReview.accreditationApplication?.accreditationType.requirementsJson &&
            !loading
        ) {
            const reqJson = JSON.parse(
                applicationReview.accreditationApplication?.accreditationType?.requirementsJson ||
                    '{}'
            );
            const specificRequirements = applicationReview.accreditationApplication?.isRenewal
                ? reqJson.reaccreditation
                : reqJson.accreditation;
            setRequirements(specificRequirements ?? reqJson);
            setFormData(reviewToForm(applicationReview));
        }
    }, [
        applicationReview.accreditationApplication?.accreditationType.requirementsJson,
        applicationReview.accreditationApplication?.isRenewal,
        loading,
    ]);

    const determineContentType = (evalGroup: IEvaluationRequirementsGroup) => {
        switch ((evalGroup.contentType || '').toLowerCase()) {
            case 'accordion':
                return AccordionInput;
            case 'singleinput':
                return SingleInput;
            case 'partd':
                return PartD;
            default:
                return evalGroup.requirements.length > 1 ? AccordionInput : SingleInput;
        }
    };

    useEffect(() => {
        if (requirements) {
            const newStepsConfig: IStepConfig[] = (requirements?.evaluation || []).map((req) => {
                return {
                    title: req.title,
                    content: determineContentType(req),
                    helpId: req.name,
                };
            });

            setStepsConfig([
                ...newStepsConfig,
                {
                    title: 'Confirmation',
                    showNextButton: false,
                    showSaveProgressButton: false,
                    content: Confirmation,
                },
            ]);
        }
    }, [requirements]);

    const handleSaveProgress = async (): Promise<OperationResult> => {
        if (readOnly) {
            return { success: true, message: '' };
        }
        if (!formData) {
            return { success: false, message: 'Cannot save blank form' };
        }

        const updatedReview: IApplicationReview = {
            ...applicationReview,
            jsonData: JSON.stringify(formData),
            reviewStatus: ReviewStatus.InProgress,
        };

        const operationResult = await update(updatedReview);

        if (operationResult.success) {
            localStorage.removeItem(applicationReview.applicationReviewId);
        } else {
            localStorage.setItem(
                applicationReview.applicationReviewId,
                JSON.stringify(updatedReview)
            );
        }

        return operationResult;
    };

    const handleInputChangeEvent = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ): void => {
        const { name, value } = event.target;
        handleInputChange(name, value);
    };

    const handleInputChange = (
        name: string,
        value:
            | string
            | string[]
            | boolean
            | ISimpleNameValuePair[]
            | IPartData<unknown>
            | IRankingsPair
    ): void => {
        const updatedFormData = {
            ...formData,
            [name]: value,
        };

        if (applicationReview.reviewFormat === ReviewFormat.Algorithm) {
            updatedFormData.score = calculateScore(updatedFormData);
        }

        setFormData(updatedFormData);
    };

    const handleSubmit = async (reviewStatus?: ReviewStatus) => {
        const rs = reviewStatus ?? ReviewStatus.Complete;

        const result = await update({
            ...applicationReview,
            jsonData: JSON.stringify(formData),
            reviewStatus: rs,
        });

        if (result.success) {
            navigate(
                '/accreditation?formSubmitType=evaluation&formSubmitResult=' + rs.toLowerCase()
            );
            return result;
        }

        return result;
    };

    const handleSetReviewFormat = async (reviewFormat: ReviewFormat): Promise<OperationResult> => {
        return await update({
            jsonData: JSON.stringify(formData),
            ...applicationReview,
            reviewFormat,
        });
    };

    const handleNextStep = (back?: boolean) => {
        return handleSetStep(
            back ? Math.max(currentStep - 1, 1) : Math.min(currentStep + 1, stepsConfig.length)
        );
    };

    const handleSetStep = async (step: number) => {
        setValidated(false);
        const result = await handleSaveProgress();

        if (!result.success) {
            return result;
        }

        if (step > currentStep) {
            const sc = stepsConfig[currentStep - 1];
            if (sc.validate) {
                const errors = sc.validate();
                if (errors.length) {
                    setValidated(true);
                    return {
                        success: false,
                        message: errors,
                    };
                }
            }
        }
        setValidated(false);
        setCurrentStep(step);

        return result;
    };

    return {
        models: {
            formData,
            currentStep,
            stepsConfig,
            requirements,
            validated,
        },
        operations: {
            handleInputChangeEvent,
            handleInputChange,
            handleNextStep,
            handleSaveProgress,
            handleSetStep,
            handleSubmit,
            handleSetReviewFormat,
        },
    };
};
